import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Heart, Search, ChevronDown } from 'lucide-react';
import GlobalSearch from './GlobalSearch';
import { useLanguage } from './LanguageContext';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const { language, t, changeLanguage, getLocalizedPath } = useLanguage();

  const isHomePage = location.pathname === '/da' || location.pathname === '/en';

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isTransparent = isHomePage && !scrolled;

  const navLinks = [
    { name: t('nav_home'), path: getLocalizedPath('home', language) },
    { name: t('nav_destinations'), path: getLocalizedPath('destinations', language) },
    { 
      name: t('nav_hotels'), 
      path: getLocalizedPath('hotels', language),
      subLinks: [
        { name: t('nav_all_hotels'), path: getLocalizedPath('hotels', language) },
        { name: t('nav_prices_safety'), path: getLocalizedPath('prices', language) }
      ]
    },
    { name: t('nav_featured_trips'), path: getLocalizedPath('featured-trips', language) },
    { name: t('nav_about'), path: getLocalizedPath('about', language) },
    { name: t('nav_traveltips'), path: getLocalizedPath('traveltips', language) },
    { name: t('nav_contact'), path: getLocalizedPath('contact', language) },
  ];

  const LanguageSelector = ({ isTransparent, className = "" }) => {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <button
          onClick={() => changeLanguage('da')}
          className={`flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all cursor-pointer ${
            language === 'da'
              ? isTransparent
                ? 'bg-white/20 text-white shadow-sm'
                : 'bg-stone-100 text-[#99A18C] shadow-sm'
              : isTransparent
                ? 'text-white/70 hover:text-white hover:bg-white/10'
                : 'text-stone-500 hover:text-[#99A18C] hover:bg-stone-50'
          }`}
        >
          <span className="text-sm leading-none">🇩🇰</span>
          <span className="leading-none">DK</span>
        </button>
        <button
          onClick={() => changeLanguage('en')}
          className={`flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all cursor-pointer ${
            language === 'en'
              ? isTransparent
                ? 'bg-white/20 text-white shadow-sm'
                : 'bg-stone-100 text-[#99A18C] shadow-sm'
              : isTransparent
                ? 'text-white/70 hover:text-white hover:bg-white/10'
                : 'text-stone-500 hover:text-[#99A18C] hover:bg-stone-50'
          }`}
        >
          <span className="text-sm leading-none">🇬🇧</span>
          <span className="leading-none">EN</span>
        </button>
      </div>
    );
  };

  return (
    <>
      <nav className={`fixed w-full z-50 transition-all duration-300 ${
        isTransparent 
          ? 'bg-transparent py-5' 
          : 'bg-white/95 backdrop-blur-md shadow-md py-3'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to={getLocalizedPath('home', language)} className="flex items-center space-x-2 group">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center group-hover:rotate-6 transition-all duration-300 bg-[#99A18C] text-white">
                <Heart className="w-6 h-6 fill-current" />
              </div>
              <div className="flex flex-col">
                <span className="text-3xl md:text-4xl font-serif font-bold leading-none transition-all duration-300 text-[#99A18C] [text-shadow:_0_1px_2px_rgba(0,0,0,0.12)]">
                  Alora <span className="italic font-light">Rejser</span>
                </span>
              </div>
            </Link>

            <div className="hidden lg:flex items-center space-x-3 xl:space-x-6">
              {navLinks.map((link) => (
                link.subLinks ? (
                  <div key={link.name} className="relative group">
                    <Link
                      to={link.path}
                      className={`inline-flex items-center gap-1 whitespace-nowrap text-xs font-bold uppercase tracking-[0.08em] xl:tracking-[0.15em] transition-all duration-300 hover:opacity-70 ${
                        location.pathname === link.path || location.pathname === getLocalizedPath('prices', language)
                          ? isTransparent 
                            ? 'text-white border-b-2 border-white'
                            : 'text-[#99A18C] border-b-2 border-[#99A18C]'
                          : isTransparent
                            ? 'text-white'
                            : 'text-[#99A18C]'
                      }`}
                    >
                      {link.name}
                      <ChevronDown size={13} className="transition-transform duration-200 group-hover:rotate-180 opacity-70" />
                    </Link>

                    {/* Dropdown Menu */}
                    <div className="absolute top-full left-0 pt-2 hidden group-hover:block transition-all duration-200 z-50">
                      <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-stone-200/80 py-2 min-w-[210px] overflow-hidden">
                        {link.subLinks.map((sub) => (
                          <Link
                            key={sub.name}
                            to={sub.path}
                            className={`block px-4 py-2.5 text-xs font-bold tracking-wider transition-colors ${
                              location.pathname === sub.path
                                ? 'text-[#99A18C] bg-stone-100/80 font-extrabold'
                                : 'text-stone-700 hover:text-[#99A18C] hover:bg-stone-50'
                            }`}
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`whitespace-nowrap text-xs font-bold uppercase tracking-[0.08em] xl:tracking-[0.15em] transition-all duration-300 hover:opacity-70 ${
                      location.pathname === link.path 
                        ? isTransparent 
                          ? 'text-white border-b-2 border-white'
                          : 'text-[#99A18C] border-b-2 border-[#99A18C]'
                        : isTransparent
                          ? 'text-white'
                          : 'text-[#99A18C]'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              
              {/* Global søgeknap på desktop */}
              <button
                onClick={() => setIsSearchOpen(true)}
                className={`p-2 rounded-full transition-all duration-300 hover:scale-105 cursor-pointer shrink-0 ${
                  isTransparent ? 'text-white hover:bg-white/10' : 'text-[#99A18C] hover:bg-stone-50'
                }`}
                aria-label={t('nav_search_aria')}
              >
                <Search size={20} />
              </button>

              <Link 
                to={getLocalizedPath('quote', language)}
                className="whitespace-nowrap px-4 xl:px-6 py-2.5 xl:py-3 rounded-full text-xs xl:text-sm font-bold uppercase tracking-wider transition-all shadow-lg hover:-translate-y-0.5 bg-[#99A18C] text-white hover:bg-[#8a917e] hover:shadow-[#99A18C]/20 shrink-0"
              >
                {t('nav_quote')}
              </Link>

              {/* Language Selector Desktop */}
              <LanguageSelector isTransparent={isTransparent} className="border-l border-stone-200 pl-3 xl:pl-4 ml-1 xl:ml-2 shrink-0" />
            </div>

            {/* Mobil søge- og menuknapper */}
            <div className="flex items-center space-x-2 lg:hidden">
              {/* Global søgeknap på mobil */}
              <button
                onClick={() => setIsSearchOpen(true)}
                className={`p-2 rounded-full transition-all duration-300 hover:scale-105 cursor-pointer ${
                  isTransparent ? 'text-white hover:bg-white/10' : 'text-[#99A18C] hover:bg-stone-50'
                }`}
                aria-label={t('nav_search_aria')}
              >
                <Search size={22} />
              </button>

              <button 
                className="p-2 cursor-pointer"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Menu"
              >
                {mobileMenuOpen ? (
                  <X className={isTransparent ? 'text-white' : 'text-[#99A18C]'} size={28} />
                ) : (
                  <Menu className={isTransparent ? 'text-white' : 'text-[#99A18C]'} size={28} />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 w-full bg-white shadow-2xl animate-in slide-in-from-top duration-300">
            <div className="flex flex-col p-6 space-y-4">
              {navLinks.map((link) => (
                link.subLinks ? (
                  <div key={link.name} className="border-b border-[#99A18C]/10 py-2">
                    <Link
                      to={link.path}
                      className="text-lg font-serif font-bold text-[#99A18C] block"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.name}
                    </Link>
                    <div className="pl-4 pt-2 pb-1 space-y-2 border-l-2 border-[#99A18C]/30 ml-2 mt-2">
                      {link.subLinks.map((sub) => (
                        <Link
                          key={sub.name}
                          to={sub.path}
                          className={`block text-sm font-medium ${
                            location.pathname === sub.path ? 'text-[#99A18C] font-bold' : 'text-stone-600 hover:text-[#99A18C]'
                          }`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    className="text-lg font-serif font-bold text-[#99A18C] border-b border-[#99A18C]/10 py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              
              {/* Language Selector Mobile */}
              <div className="py-2 border-b border-[#99A18C]/10 flex justify-between items-center">
                <span className="text-sm font-bold text-stone-500 uppercase tracking-wider">Sprog / Language</span>
                <LanguageSelector isTransparent={false} />
              </div>

              <Link 
                to={getLocalizedPath('quote', language)}
                className="bg-[#99A18C] text-white px-6 py-4 rounded-xl text-center font-bold"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav_quote')}
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Global søgemodal */}
      <GlobalSearch isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} initialQuery="" />
    </>
  );
};

export default Navbar;
